import { useEffect, useState } from 'react';

const UNKNOWN = 'unknown';
const FRESH_FOR_MS = 30_000;

/** Participant chat-session presence, never the observer's connection status. */
export default function useParticipantPresence({ socket, connected, conversation }) {
  const conversationId = conversation?.conversation_id;
  const participantId = conversation?.other_party?.id;
  const participantType = conversation?.other_party?.type;
  const [snapshot, setSnapshot] = useState(null);
  useEffect(() => {
    setSnapshot(null);
    if (!connected || !socket || !conversationId || !participantId || !participantType) return undefined;
    let disposed = false;
    let pending = false;
    let expiry;
    const unknown = () => { clearTimeout(expiry); if (!disposed) setSnapshot(null); };
    const refresh = () => {
      if (disposed || pending || document.visibilityState === 'hidden') return;
      if (!socket.connected) { unknown(); return; }
      pending = true;
      const requestedAt = Date.now();
      socket.timeout(8000).emit('conversation_presence', { conversation_id: conversationId }, (error, response) => {
        pending = false;
        if (disposed) return;
        const value = response?.presence;
        if (error || !socket.connected || !response?.ok || value?.conversation_id !== conversationId
          || value?.participant_id !== participantId || value?.participant_type !== participantType
          || !['online', 'offline'].includes(value?.state) || Date.now() - requestedAt >= FRESH_FOR_MS) {
          unknown(); return;
        }
        clearTimeout(expiry);
        setSnapshot({ conversationId, participantId, state: value.state, expiresAt: requestedAt + FRESH_FOR_MS });
        expiry = setTimeout(unknown, Math.max(0, requestedAt + FRESH_FOR_MS - Date.now()));
      });
    };
    const visible = () => { unknown(); refresh(); };
    socket.on('disconnect', unknown);
    socket.on('connect_error', unknown);
    document.addEventListener('visibilitychange', visible);
    const interval = setInterval(refresh, 10_000);
    refresh();
    return () => {
      disposed = true;
      clearInterval(interval);
      clearTimeout(expiry);
      socket.off('disconnect', unknown);
      socket.off('connect_error', unknown);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [socket, connected, conversationId, participantId, participantType]);
  return connected && snapshot && snapshot.conversationId === conversationId && snapshot?.participantId === participantId
    && Date.now() < snapshot.expiresAt ? snapshot.state : UNKNOWN;
}
